import { z } from "zod";
import { schemes } from "@zeal/common";

export enum EInvoiceTemplateStatus {
  Draft = "draft",
  Active = "active",
  Archived = "archived",
}

export enum EInvoicePaymentMethod {
  ACH = "ach",
  Card = "card",
}

export enum EDiscountFeeType {
  FlatAmountDiscount = "Flat amount discount",
  PercentDiscount = "Percent discount",
  FlatAmountFee = "Flat amount fee",
  PercentFee = "Percent fee",
  DailyInterestFee = "Daily interest fee",
}

// Step 1: Name
const InvoiceTemplateNameSchema = z.object({
  name: z.string().min(1, "Invoice template name is required").max(100),
  description: z.string().optional(),
});

// Step 2: Invoice For (flat items)
const InvoiceItemSchema = z.object({
  id: z.string(),
  description: z.string(),
  quantity: z.number().optional(),
  price: z.number().optional(),
  is_removable: z.boolean().optional(),
});

const InvoiceForSchema = z.object({
  completed_pay: z.boolean(),
  employee_payroll: z.boolean().optional(),
  off_cycle_payroll: z.boolean().optional(),
  contractor_payments: z.boolean().optional(),
  date_range: z.string().optional(),
  items: z.array(InvoiceItemSchema).optional(),
});

// Step 3: Configuration (can have children)
const ConfigChildItemSchema = z.object({
  id: z.string(),
  description: z.string(),
  is_removable: z.boolean().optional(),
});

const ConfigItemSchema = z.object({
  id: z.string(),
  description: z.string(),
  is_removable: z.boolean().optional(),
  children: z.array(ConfigChildItemSchema).optional(),
});

const ConfigurationSchema = z.object({
  separate_by: z.string().optional(),
  employee_items: z.array(ConfigItemSchema),
  contractor_items: z.array(ConfigItemSchema),
});

// Step 4: Discounts & Fees
const DiscountFeeItemSchema = z.object({
  id: z.string(),
  kind: z.enum(["discount", "fee"]),
  name: z.string().min(1).max(100),
  type: z.nativeEnum(EDiscountFeeType),
  amount: z.number().positive(),
  apply_only_when_overdue: z.boolean().optional(),
});

export const DiscountsAndFeesSchema = z.object({
  discounts: z.array(DiscountFeeItemSchema).optional().default([]),
  fees: z.array(DiscountFeeItemSchema).optional().default([]),
});

// Step 5: Payment
export const PaymentSchema = z.object({
  invoice_date: z.string().default("Day invoices are sent"),
  payment_terms: z.string().default("net_30"),
  all: z.boolean().default(true),
  bank_account: z.boolean().default(true),
  card_payment: z.boolean().default(true),
});

// Step 6: Notes
export const NotesSchema = z.object({
  payer_memo: z.string().max(250).optional(),
  internal_note: z.string().max(250).optional(),
});

// Step 7: Approval & Delivery
export const ApprovalAndDeliverySchema = z.object({
  require_approval: z.boolean().default(true),
  unpaid_reminder_frequency: z.string().default("weekly"),
  overdue_reminder_frequency: z.string().default("every_three_days"),
});

// Invoice Template schema
export const createInvoiceTemplateRequestSchema = z.object({
  companyID: schemes.companyID(),
  name: InvoiceTemplateNameSchema,
  customers: z
    .object({
      selectedCustomerIds: z.array(schemes.uuid()).default([]),
    })
    .optional(),
  invoice_for: InvoiceForSchema,
  configuration: ConfigurationSchema,
  discounts_and_fees: DiscountsAndFeesSchema,
  payment: PaymentSchema,
  notes: NotesSchema,
  approval_and_delivery: ApprovalAndDeliverySchema,
  status: z
    .nativeEnum(EInvoiceTemplateStatus)
    .default(EInvoiceTemplateStatus.Draft),
});

export const createInvoiceTemplateResponseSchema =
  createInvoiceTemplateRequestSchema.extend({
    template_id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
  });

export type TCreateInvoiceTemplateRequest = z.infer<
  typeof createInvoiceTemplateRequestSchema
>;
export type TCreateInvoiceTemplateResponse = z.infer<
  typeof createInvoiceTemplateResponseSchema
>;
